import {
  FetchResult,
  gql,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationResult,
  TypedDocumentNode,
  useMutation,
} from '@apollo/client';
import {
  SetSalesPartnerPermissionMutation,
  SetSalesPartnerPermissionMutationVariables,
  UpdateSalesPartnerPermissionMutation,
  UpdateSalesPartnerPermissionMutationVariables,
} from '../../generated/graphql';

export const salesPartnerPermissionMutation: TypedDocumentNode<
  SetSalesPartnerPermissionMutation,
  SetSalesPartnerPermissionMutationVariables
> = gql`
  mutation SetSalesPartnerPermission($permissionId: ID!, $granted: Boolean!) {
    setSalesPartnerPermission(permissionId: $permissionId, granted: $granted) {
      id
      permissionId
      granted
    }
  }
`;

export const salesPartnerPermissionUpdateMutation: TypedDocumentNode<
  UpdateSalesPartnerPermissionMutation,
  UpdateSalesPartnerPermissionMutationVariables
> = gql`
  mutation UpdateSalesPartnerPermission($contractPartnerNumber: Int!, $permissionId: ID!, $granted: Boolean!) {
    updateSalesPartnerPermission(
      contractPartnerNumber: $contractPartnerNumber
      permissionId: $permissionId
      granted: $granted
    ) {
      id
      permissionId
      granted
    }
  }
`;

export function useSalesPartnerPermissionMutation(
  options?: MutationHookOptions<SetSalesPartnerPermissionMutation, SetSalesPartnerPermissionMutationVariables>,
): [
  (
    permissionId: string,
    granted: boolean,
    options?: MutationFunctionOptions<SetSalesPartnerPermissionMutation, SetSalesPartnerPermissionMutationVariables>,
  ) => Promise<FetchResult<SetSalesPartnerPermissionMutation>>,
  MutationResult<SetSalesPartnerPermissionMutation>,
] {
  const [update, response] = useMutation<SetSalesPartnerPermissionMutation, SetSalesPartnerPermissionMutationVariables>(
    salesPartnerPermissionMutation,
    options,
  );

  return [
    async (permissionId, granted, opts) => {
      // id functions as cache key; in case of sales partner, it is the SHA-1 of the permission id
      const source = new TextEncoder().encode(permissionId);
      const digest = await crypto.subtle.digest('SHA-1', source);
      const result = [...new Uint8Array(digest)];
      const id = result.map((digit) => digit.toString(16).padStart(2, '0')).join('');

      return update({
        ...opts,
        variables: {
          permissionId,
          granted,
        },
        optimisticResponse: {
          setSalesPartnerPermission: {
            __typename: 'AssignedPermission',
            id,
            permissionId,
            granted,
          },
        },
      });
    },
    response,
  ];
}

export function useSalesPartnerPermissionUpdateMutation(
  options?: MutationHookOptions<UpdateSalesPartnerPermissionMutation, UpdateSalesPartnerPermissionMutationVariables>,
): [
  (
    contractPartnerNumber: number,
    permissionId: string,
    granted: boolean,
    options?: MutationFunctionOptions<
      UpdateSalesPartnerPermissionMutation,
      UpdateSalesPartnerPermissionMutationVariables
    >,
  ) => Promise<FetchResult<UpdateSalesPartnerPermissionMutation>>,
  MutationResult<UpdateSalesPartnerPermissionMutation>,
] {
  const [update, response] = useMutation<
    UpdateSalesPartnerPermissionMutation,
    UpdateSalesPartnerPermissionMutationVariables
  >(salesPartnerPermissionUpdateMutation, options);

  return [
    async (contractPartnerNumber, permissionId, granted, opts) => {
      // ID functions as cache key; in case of sales partners, it is the SHA-1 of the permission ID
      const source = new TextEncoder().encode(permissionId);
      const digest = await crypto.subtle.digest('SHA-1', source);
      const result = [...new Uint8Array(digest)];
      const id = result.map((digest) => digest.toString(16).padStart(2, '0')).join('');

      return update({
        ...opts,
        variables: {
          contractPartnerNumber,
          permissionId,
          granted,
        },
        optimisticResponse: {
          updateSalesPartnerPermission: {
            __typename: 'AssignedPermission',
            id,
            permissionId,
            granted,
          },
        },
      });
    },
    response,
  ];
}
